import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Lock, Eye, Database, Share2, Shield, Globe, Cookie, Mail, UserCheck, Scale, FileText } from 'lucide-react';

const PrivacyPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-16 bg-gradient-to-b from-secondary to-background">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 mb-6">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: February 2026</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4">
            <div className="prose prose-invert max-w-none space-y-8">
              
              {/* Introduction */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-emerald-400" />
                  1. Introduction
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  At FundTheHope ("we," "us," or "our"), we take your privacy seriously. This Privacy Policy explains how we collect, use, 
                  disclose, and safeguard your information when you use our crowdfunding platform (the "Platform"). Please read this 
                  policy carefully. By using FundTheHope, you consent to the practices described in this policy.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  <strong className="text-foreground">Data Controller:</strong> FundTheHope is the data controller responsible for your personal data. 
                  For users in the European Economic Area (EEA), we process your data in accordance with the General Data Protection Regulation (GDPR).
                </p>
              </div>

              {/* Information We Collect */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Database className="w-6 h-6 text-emerald-400" />
                  2. Information We Collect
                </h2>
                <div className="space-y-6 text-muted-foreground">
                  <div>
                    <p className="font-semibold text-foreground mb-2">Personal Information You Provide:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Account Data:</strong> Name, email address, password, phone number</li>
                      <li><strong>Profile Information:</strong> Profile photo, bio, location</li>
                      <li><strong>Identity Verification:</strong> Government-issued ID, date of birth, address (for KYC)</li>
                      <li><strong>Payment Information:</strong> Bank account details, payment card information (processed securely by our payment partners)</li>
                      <li><strong>Campaign Content:</strong> Campaign descriptions, photos, videos, updates</li>
                      <li><strong>Communications:</strong> Messages, support requests, feedback</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-2">Information Collected Automatically:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
                      <li><strong>Usage Data:</strong> Pages visited, time spent, clicks, search queries</li>
                      <li><strong>Location Data:</strong> General location based on IP address</li>
                      <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies (subject to your consent where required)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* GDPR Article 6 Legal Bases - NEW */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Scale className="w-6 h-6 text-emerald-400" />
                  3. Legal Basis for Processing (GDPR Article 6)
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>We process your personal data only when we have a valid legal basis under applicable law. For users in the EEA, UK, and other jurisdictions that require a legal basis, we rely on the following grounds under GDPR Article 6:</p>
                  
                  <div className="bg-secondary/30 rounded-lg p-4 space-y-4">
                    <div>
                      <p className="font-semibold text-emerald-400">6(1)(a) Consent</p>
                      <p className="text-sm mt-1">Where you have given clear consent for us to process your personal data for a specific purpose. This applies to:</p>
                      <ul className="list-disc pl-6 text-sm mt-2 space-y-1">
                        <li>Marketing communications and newsletters</li>
                        <li>Analytics cookies and tracking technologies</li>
                        <li>Marketing/advertising cookies</li>
                        <li>Sharing your data with third-party marketing partners</li>
                        <li>Processing special category data (where applicable)</li>
                      </ul>
                      <p className="text-sm mt-2 italic">You may withdraw consent at any time via your account settings or by contacting us.</p>
                    </div>
                    
                    <div>
                      <p className="font-semibold text-emerald-400">6(1)(b) Contract Performance</p>
                      <p className="text-sm mt-1">Processing necessary for the performance of a contract with you or to take steps at your request before entering into a contract. This applies to:</p>
                      <ul className="list-disc pl-6 text-sm mt-2 space-y-1">
                        <li>Creating and managing your account</li>
                        <li>Processing donations and payments</li>
                        <li>Facilitating campaign creation and management</li>
                        <li>Processing withdrawal requests</li>
                        <li>Providing customer support related to your account or transactions</li>
                        <li>Sending transactional communications (receipts, confirmations, updates)</li>
                      </ul>
                    </div>
                    
                    <div>
                      <p className="font-semibold text-emerald-400">6(1)(c) Legal Obligation</p>
                      <p className="text-sm mt-1">Processing necessary for compliance with a legal obligation to which we are subject. This applies to:</p>
                      <ul className="list-disc pl-6 text-sm mt-2 space-y-1">
                        <li>Identity verification (KYC) and anti-money laundering (AML) checks</li>
                        <li>Tax reporting and record-keeping requirements</li>
                        <li>Responding to valid legal requests from law enforcement or regulatory authorities</li>
                        <li>Fraud prevention and detection as required by financial regulations</li>
                        <li>Data retention as required by applicable laws</li>
                      </ul>
                    </div>
                    
                    <div>
                      <p className="font-semibold text-emerald-400">6(1)(d) Vital Interests</p>
                      <p className="text-sm mt-1">Processing necessary to protect the vital interests of you or another person. This may apply in emergency situations where we need to process data to protect someone's life or health.</p>
                    </div>
                    
                    <div>
                      <p className="font-semibold text-emerald-400">6(1)(f) Legitimate Interests</p>
                      <p className="text-sm mt-1">Processing necessary for our legitimate interests or those of a third party, except where such interests are overridden by your interests or fundamental rights. This applies to:</p>
                      <ul className="list-disc pl-6 text-sm mt-2 space-y-1">
                        <li>Improving and optimizing the Platform (bug fixes, feature development)</li>
                        <li>Understanding how users interact with our services (aggregated analytics)</li>
                        <li>Detecting, preventing, and investigating fraud, security incidents, and abuse</li>
                        <li>Enforcing our Terms of Service and policies</li>
                        <li>Protecting our legal rights and defending against legal claims</li>
                        <li>Business communications and customer relationship management</li>
                        <li>Essential security measures (authentication, access controls)</li>
                      </ul>
                      <p className="text-sm mt-2 italic">We conduct a balancing test to ensure our legitimate interests do not override your fundamental rights and freedoms.</p>
                    </div>
                  </div>
                  
                  <p className="mt-4"><strong className="text-foreground">Processing Summary Table:</strong></p>
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full text-sm border border-border">
                      <thead className="bg-secondary/50">
                        <tr>
                          <th className="text-left p-3 border-b border-border">Processing Activity</th>
                          <th className="text-left p-3 border-b border-border">Legal Basis</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Account creation & management</td>
                          <td className="p-3">Contract (6(1)(b))</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Payment processing</td>
                          <td className="p-3">Contract (6(1)(b))</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Identity verification (KYC)</td>
                          <td className="p-3">Legal Obligation (6(1)(c))</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Fraud prevention</td>
                          <td className="p-3">Legal Obligation (6(1)(c)) / Legitimate Interests (6(1)(f))</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Analytics cookies</td>
                          <td className="p-3">Consent (6(1)(a))</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Marketing communications</td>
                          <td className="p-3">Consent (6(1)(a))</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Platform security</td>
                          <td className="p-3">Legitimate Interests (6(1)(f))</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="p-3">Service improvement</td>
                          <td className="p-3">Legitimate Interests (6(1)(f))</td>
                        </tr>
                        <tr>
                          <td className="p-3">Legal compliance & reporting</td>
                          <td className="p-3">Legal Obligation (6(1)(c))</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* How We Use Information */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Eye className="w-6 h-6 text-emerald-400" />
                  4. How We Use Your Information
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>We use the information we collect for the following purposes:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Provide Services:</strong> Process donations, manage campaigns, facilitate withdrawals</li>
                    <li><strong>Verify Identity:</strong> Prevent fraud and comply with legal requirements (KYC/AML)</li>
                    <li><strong>Communicate:</strong> Send transaction confirmations, updates, and support responses</li>
                    <li><strong>Improve Platform:</strong> Analyze usage patterns, fix bugs, develop new features</li>
                    <li><strong>Personalize Experience:</strong> Show relevant campaigns, customize recommendations</li>
                    <li><strong>Marketing:</strong> Send promotional content (only with your explicit consent)</li>
                    <li><strong>Legal Compliance:</strong> Respond to legal requests, enforce our terms</li>
                    <li><strong>Security:</strong> Detect, prevent, and investigate fraud and security incidents</li>
                  </ul>
                </div>
              </div>

              {/* Information Sharing */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Share2 className="w-6 h-6 text-emerald-400" />
                  5. Information Sharing & Disclosure
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p className="font-semibold text-foreground">We may share your information with:</p>
                  <ul className="list-disc pl-6 space-y-3">
                    <li>
                      <strong>Other Users:</strong> Your public profile, campaign details, and donation activity (if not anonymous) 
                      are visible to other users.
                    </li>
                    <li>
                      <strong>Service Providers:</strong> Payment processors (Stripe), email services (Resend), 
                      SMS providers (Twilio), cloud hosting, and analytics providers. These providers process data on our behalf under data processing agreements.
                    </li>
                    <li>
                      <strong>Legal Requirements:</strong> When required by law, court order, or government request.
                    </li>
                    <li>
                      <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.
                    </li>
                    <li>
                      <strong>With Your Consent:</strong> When you explicitly authorize us to share your information.
                    </li>
                  </ul>
                  <p className="mt-4 text-emerald-400 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    We never sell your personal information to third parties.
                  </p>
                </div>
              </div>

              {/* International Data Transfers */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Globe className="w-6 h-6 text-emerald-400" />
                  6. International Data Transfers
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>Your information may be transferred to and processed in countries other than your own, including the United States.</p>
                  <p><strong className="text-foreground">EEA/UK Users:</strong> When we transfer your data outside the EEA or UK, we ensure appropriate safeguards are in place, including:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Adequacy Decisions:</strong> Transfers to countries deemed adequate by the European Commission</li>
                    <li><strong>Standard Contractual Clauses (SCCs):</strong> EU-approved model clauses with data importers</li>
                    <li><strong>Supplementary Measures:</strong> Additional technical and organizational safeguards where required</li>
                  </ul>
                  <p>You may request a copy of the safeguards we use for international transfers by contacting us at privacy@fundthehope.com.</p>
                </div>
              </div>

              {/* Data Security */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Lock className="w-6 h-6 text-emerald-400" />
                  7. Data Security
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>We implement industry-standard security measures to protect your data:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Encryption:</strong> All data transmitted via HTTPS/TLS encryption</li>
                    <li><strong>Secure Storage:</strong> Passwords hashed using bcrypt, sensitive data encrypted at rest</li>
                    <li><strong>Payment Security:</strong> PCI-DSS compliant payment processing through trusted partners</li>
                    <li><strong>Access Controls:</strong> Limited employee access to personal data on a need-to-know basis</li>
                    <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
                  </ul>
                  <p className="mt-4">
                    While we strive to protect your information, no method of transmission over the Internet is 100% secure. 
                    We cannot guarantee absolute security.
                  </p>
                </div>
              </div>

              {/* Cookies */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Cookie className="w-6 h-6 text-emerald-400" />
                  8. Cookies & Tracking Technologies
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>We use cookies and similar technologies categorized as follows:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Essential Cookies:</strong> Required for basic platform functionality (authentication, security). These do not require consent.</li>
                    <li><strong>Preference Cookies:</strong> Remember your settings and preferences. These do not require consent where they are strictly necessary.</li>
                    <li><strong>Analytics Cookies:</strong> Understand how users interact with our platform. <span className="text-emerald-400">Requires your consent.</span></li>
                    <li><strong>Marketing Cookies:</strong> Deliver relevant advertisements and track campaign performance. <span className="text-emerald-400">Requires your consent.</span></li>
                  </ul>
                  <p className="mt-4">
                    You can manage your cookie preferences through our cookie consent banner or your browser settings. 
                    Disabling essential cookies may affect platform functionality.
                  </p>
                </div>
              </div>

              {/* Your Rights */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <UserCheck className="w-6 h-6 text-emerald-400" />
                  9. Your Privacy Rights
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>Under GDPR and other applicable data protection laws, you have the following rights:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Right of Access (Art. 15):</strong> Request a copy of the personal data we hold about you</li>
                    <li><strong>Right to Rectification (Art. 16):</strong> Update or correct inaccurate information</li>
                    <li><strong>Right to Erasure (Art. 17):</strong> Request deletion of your personal data (subject to legal requirements and legitimate interests)</li>
                    <li><strong>Right to Restriction (Art. 18):</strong> Request we limit how we process your data</li>
                    <li><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format</li>
                    <li><strong>Right to Object (Art. 21):</strong> Object to processing based on legitimate interests or for direct marketing</li>
                    <li><strong>Right to Withdraw Consent (Art. 7):</strong> Withdraw consent at any time where processing is based on consent</li>
                    <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
                  </ul>
                  <p className="mt-4">
                    <strong className="text-foreground">How to Exercise Your Rights:</strong> Submit requests to <a href="mailto:privacy@fundthehope.com" className="text-emerald-400 hover:underline">privacy@fundthehope.com</a>. 
                    We will respond within 30 days (or as required by applicable law). We may need to verify your identity before processing requests.
                  </p>
                </div>
              </div>

              {/* Data Retention */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-emerald-400" />
                  10. Data Retention
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>We retain your personal information for as long as necessary to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide our services to you</li>
                    <li>Comply with legal obligations (tax records: 7 years; fraud prevention: as required)</li>
                    <li>Resolve disputes and enforce our agreements</li>
                  </ul>
                  <p><strong className="text-foreground">Retention Periods:</strong></p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Account Data:</strong> Duration of account + 30 days after deletion request</li>
                    <li><strong>Transaction Data:</strong> 7 years (tax and financial regulations)</li>
                    <li><strong>KYC Documents:</strong> 5 years after account closure (AML requirements)</li>
                    <li><strong>Support Communications:</strong> 3 years</li>
                    <li><strong>Analytics Data:</strong> 26 months (anonymized thereafter)</li>
                  </ul>
                  <p className="mt-4">
                    When you delete your account, we will delete or anonymize your personal data within 30 days, 
                    except where retention is required by law.
                  </p>
                </div>
              </div>

              {/* Children */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4">11. Children's Privacy</h2>
                <p className="text-muted-foreground">
                  FundTheHope is not intended for users under 18 years of age. We do not knowingly collect personal 
                  information from children. If we become aware that we have collected data from a child without 
                  parental consent, we will take steps to delete that information.
                </p>
              </div>

              {/* Changes */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4">12. Changes to This Policy</h2>
                <p className="text-muted-foreground">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                  posting the new policy on this page, updating the "Last updated" date, and (where required) seeking your consent for new processing activities. 
                  We encourage you to review this policy periodically.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-r from-emerald-500/10 to-teal-600/10 border border-emerald-500/30 rounded-xl p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Mail className="w-6 h-6 text-emerald-400" />
                  13. Contact Us
                </h2>
                <p className="text-muted-foreground mb-4">
                  If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-2 text-muted-foreground">
                  <p><strong className="text-foreground">Privacy Team:</strong> privacy@fundthehope.com</p>
                  <p><strong className="text-foreground">Data Protection Officer:</strong> dpo@fundthehope.com</p>
                  <p><strong className="text-foreground">General Support:</strong> support@fundthehope.com</p>
                </div>
                <p className="text-muted-foreground mt-4">
                  <strong className="text-foreground">Supervisory Authority:</strong> If you are in the EEA and believe we have not adequately addressed your concerns, 
                  you have the right to lodge a complaint with your local data protection authority.
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
